// libraries
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import styled from 'styled-components';

import {
	DatePicker,
	Button,
	Form,
	Input,
	notification,
	Select,
	Switch,
	Tag,
	FormInstance,
} from 'antd';
import { ArrowRightOutlined, CheckOutlined, DeleteFilled } from '@ant-design/icons';
import * as Colors from '@ant-design/colors';
import { FieldData } from 'rc-field-form/lib/interface';

// services
import * as ActivityService from '../../../../../services/bb-api/activity';
import * as StatusService from '../../../../../services/bb-api/status';
import * as MunicipalityService from '../../../../../services/bb-api/municipality';

// components
import { Spinner } from '../../../../shared/spinner';
import { DeleteModal } from '../../../../shared/delete-modal';
import { AddressSearchBar, Address } from '../../../../shared/address-search-bar';

import { txt } from '../../../../../locales/i18n';
import { useDeviceSize } from '../../../../../hooks/device-size';

export function ActivityFormTab({ useActivity }: any): JSX.Element {
	const [activity, setActivity] = useActivity;
	const [municipalities, setMunicipalities] = useState<Record<string, any>[]>([]);
	const [statuses, setStatuses] = useState<Record<string, any>[]>([]);
	const [categories, setCategories] = useState<Record<string, any>[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const isMobileDevice: boolean = useDeviceSize();

	const [updateLoading, setUpdateLoading] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

	const formRef = useRef<any>(null);
	const history = useHistory();
	const params = useParams<any>();
	const activityId: number = params.id;

	useEffect(() => {
		async function getActivity(): Promise<void> {
			setLoading(true);

			const statuses: Record<string, any>[] = await StatusService.query();
			setStatuses(statuses);

			const categories: Record<string, any>[] = await ActivityService.getCategories();
			setCategories(categories);

			const municipalities: Record<string, any>[] = await MunicipalityService.query();
			setMunicipalities(municipalities);

			setLoading(false);
		}

		getActivity();
	}, [activityId, history, setActivity]);

	function handleCategoryChange(values: number[]): void {
		const form: FormInstance = formRef.current;

		if (values.length === 0) {
			form.setFields([
				{
					name: 'categories',
					errors: [txt('notification.minimum x required', { amount: 1 })],
				},
			]);
			return;
		}

		form.setFields([{ name: 'categories', errors: [] }]);
	}

	async function handleUpdate(values: any): Promise<void> {
		const form: FormInstance | null = formRef.current;
		if (form === null) return;

		form.setFields([{ name: 'lat', errors: [] }]);
		const lat = form.getFieldValue('lat') ?? null;

		if (lat === null) {
			form.setFields([{ name: 'lat', errors: [txt('global.required')] }]);
			return;
		}

		if (values.categories.length === 0) {
			formRef.current.setFields([
				{
					name: 'categories',
					errors: [txt('notification.minimum x required', { amount: 1 })],
				},
			]);
			return;
		}

		setUpdateLoading(true);

		try {
			await ActivityService.update(activityId, values);
			notification.success({ message: txt('notification.update successful') });
			setActivity({ ...activity, ...values });
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setUpdateLoading(false);
	}

	async function handleDelete(): Promise<void> {
		await ActivityService.remove(activityId);
		history.replace('/activities');
	}

	function renderDate(): JSX.Element {
		if (activity.isRecurring === true) {
			return <Tag color="blue">{txt('global.recurring')}</Tag>;
		}

		return (
			<DatePicker.RangePicker
				showTime
				showSecond={false}
				format="ddd DD/MM-YY HH:mm"
				disabled
				style={{ width: '100%' }}
			/>
		);
	}

	function handleNavigateLink(): void {
		const link: string | null = formRef?.current?.getFieldValue('url');
		if (link === null) return;

		if (/^http/g.test(link)) {
			window?.open(link, '_blank')?.focus();
			return;
		}

		window?.open('http://' + link, '_blank')?.focus();
	}

	const handleAddressSelect = (address: Address | null) => {
		const form = formRef.current;
		if (form === null) return;

		if (address === null) {
			const addressFields: FieldData[] = [
				{ name: 'city', value: null },
				{ name: 'address', value: null },
				{ name: 'postal_code', value: null },
				{ name: 'lat', value: null },
				{ name: 'lng', value: null },
			];

			return form?.setFields(addressFields);
		}

		const addressFields: FieldData[] = [
			{ name: 'city', value: address.city },
			{ name: 'address', value: address.name },
			{ name: 'postal_code', value: address.postalCode },
			{ name: 'lat', value: address.lat },
			{ name: 'lng', value: address.lng },
		];

		form?.setFields(addressFields);
	};

	if (loading === true) return <Spinner />;

	return (
		<StyledForm
			ref={formRef}
			initialValues={activity}
			onFinish={handleUpdate}
			labelCol={{ span: 5 }}
			isMobileDevice={isMobileDevice}
		>
			<div className="input-container">
				<div>
					<Form.Item label={txt('global.title')} name="title">
						<Input required />
					</Form.Item>

					<Form.Item label={txt('global.organizer')} name="organizer">
						<Input required />
					</Form.Item>

					<Form.Item label={txt('global.date')} name="dates">
						{renderDate()}
					</Form.Item>

					<Form.Item label={txt('global.location')} name="location">
						<Input required />
					</Form.Item>

					<Form.Item label={txt('global.search')}>
						<AddressSearchBar onSelect={handleAddressSelect} />
					</Form.Item>

					<Form.Item name="address" label={txt('global.address')}>
						<Input placeholder={txt('global.address')} disabled />
					</Form.Item>

					<Form.Item label={txt('global.city')}>
						<div className="multi-input-container">
							<Form.Item name="postal_code" noStyle>
								<Input
									placeholder={txt('global.postal code')}
									disabled
									className="postalcode-input"
								/>
							</Form.Item>

							<Form.Item name="city" noStyle>
								<Input placeholder={txt('global.city')} disabled />
							</Form.Item>
						</div>
					</Form.Item>

					<Form.Item label="GPS">
						<div className="multi-input-container">
							<Form.Item name="lat" noStyle>
								<Input disabled placeholder="latitude" />
							</Form.Item>

							<Form.Item name="lng" noStyle>
								<Input disabled placeholder="longitude" />
							</Form.Item>
						</div>
					</Form.Item>

					<Form.Item label={txt('global.municipality')} name="municipality_id">
						<Select>
							{municipalities.map((municipality: any) => (
								<Select.Option key={municipality.id} value={municipality.id}>
									{municipality.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label={txt('global.price')} name="ticket_price">
						<Input type="number" required />
					</Form.Item>

					<Form.Item label={txt('global.categories')} name="categories">
						<Select
							mode="multiple"
							placeholder="Please select"
							style={{ width: '100%' }}
							onChange={handleCategoryChange}
						>
							{categories.map((category: any) => (
								<Select.Option key={category.id} value={category.id}>
									{category.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item label={txt('global.website')}>
						<Input.Group compact style={{ display: 'flex' }}>
							<Form.Item name="url" style={{ flex: 1 }} noStyle>
								<Input />
							</Form.Item>

							<Form.Item noStyle>
								<Button type="ghost" onClick={handleNavigateLink}>
									<ArrowRightOutlined />
								</Button>
							</Form.Item>
						</Input.Group>
					</Form.Item>

					<Form.Item label="Status" name="status_id">
						<Select>
							{statuses.map((status: Record<string, any>) => (
								<Select.Option key={status.id} value={status.id}>
									{status.name}
								</Select.Option>
							))}
						</Select>
					</Form.Item>
				</div>

				<Form.Item label="Body" name="body" noStyle>
					<Input.TextArea style={{ resize: 'none' }} required />
				</Form.Item>
			</div>

			<Form.Item>
				<div className="submit-container">
					<Form.Item name="approved" valuePropName="checked">
						<Switch
							checkedChildren={txt('global.approved')}
							unCheckedChildren={txt('global.not approved')}
							defaultChecked={activity.approved === 1}
						/>
					</Form.Item>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						type="primary"
						htmlType="submit"
						loading={updateLoading}
					>
						{txt('global.update')} <CheckOutlined />
					</Button>

					<Button
						size={isMobileDevice ? 'large' : 'middle'}
						danger
						type="ghost"
						onClick={() => setShowDeleteModal(true)}
						className="delete-button"
					>
						{txt('global.delete')} <DeleteFilled />
					</Button>
				</div>
			</Form.Item>

			<DeleteModal
				onConfirm={handleDelete}
				useVisible={[showDeleteModal, setShowDeleteModal]}
			/>
		</StyledForm>
	);
}

type StyledFormProps = {
	isMobileDevice: boolean;
};

const StyledForm = styled(Form)<StyledFormProps>`
	display: flex;
	flex-direction: column;
	gap: 2em;

	.input-container {
		display: grid;
		gap: 0rem 2rem;

		${({ isMobileDevice }) => {
			if (isMobileDevice) return;
			return 'grid-template-columns: repeat(auto-fill, minmax(350px, 1fr))';
		}}
	}

	.multi-input-container {
		display: flex;

		> * {
			flex: 1;
		}

		.postalcode-input {
			width: 12ch;
			flex: none;
		}
	}

	.submit-container {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};

		button.ant-switch {
			zoom: 1.4;

			span {
				font-size: 0.65em;
			}

			&.ant-switch-checked {
				background: ${Colors.green.primary};
			}
		}

		.delete-button {
			margin-left: ${({ isMobileDevice }) => (isMobileDevice ? '0' : 'auto')};
		}
	}
`;
